import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import styles from './RowMenu.module.css';
import useDismissableMenu from '../../app-shell/useDismissableMenu';

const KebabIcon = (props) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
        <circle cx="5" cy="12" r="1.8"/>
        <circle cx="12" cy="12" r="1.8"/>
        <circle cx="19" cy="12" r="1.8"/>
    </svg>
);

const MENU_WIDTH = 212;
const GAP = 8;
const EDGE_MARGIN = 8;

/**
 * The row ⋮ menu: a popover on desktop (flips upward when there isn't room
 * below), a bottom action sheet on mobile (same markup, CSS repositions it).
 * `items`: [{key, label, icon, onSelect, danger}].
 *
 * Rendered through a portal into document.body and positioned with
 * `position: fixed` from the trigger's own bounding rect, rather than
 * `position: absolute` inside the row - DataListCard clips overflow to keep
 * its rounded corners, which otherwise clips this menu (and, when it flips
 * upward, can paint over the header row above it) whenever it doesn't fit
 * inside the card's own box.
 */
const RowMenu = ({label, items, open, onOpenChange}) => {
    const {triggerRef, menuRef} = useDismissableMenu(open, () => onOpenChange(false));
    const [position, setPosition] = useState(null);

    useEffect(() => {
        if (!open || !triggerRef.current) return undefined;

        const updatePosition = () => {
            if (!triggerRef.current) return;
            if (window.innerWidth < 768) {
                // Mobile is a fixed bottom sheet, positioned entirely by CSS.
                setPosition(null);
                return;
            }
            const rect = triggerRef.current.getBoundingClientRect();
            const estimatedMenuHeight = items.length * 40 + 40;
            const openUp = rect.bottom + estimatedMenuHeight > window.innerHeight && rect.top > estimatedMenuHeight;
            const left = Math.min(
                Math.max(EDGE_MARGIN, rect.right - MENU_WIDTH),
                window.innerWidth - MENU_WIDTH - EDGE_MARGIN
            );
            setPosition(openUp
                ? {left, bottom: window.innerHeight - rect.top + GAP}
                : {left, top: rect.bottom + GAP});
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        return () => window.removeEventListener('resize', updatePosition);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        // The menu's position is computed once, from the trigger's rect at
        // open time - close rather than let it drift out of sync if the
        // page scrolls underneath it.
        const close = () => onOpenChange(false);
        window.addEventListener('scroll', close, true);
        return () => window.removeEventListener('scroll', close, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
            const menuItems = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);
            const currentIndex = menuItems.indexOf(document.activeElement);
            if (currentIndex === -1) return;
            e.preventDefault();
            const nextIndex = e.key === 'ArrowDown'
                ? (currentIndex + 1) % menuItems.length
                : (currentIndex - 1 + menuItems.length) % menuItems.length;
            menuItems[nextIndex].focus();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const select = (item) => {
        onOpenChange(false);
        item.onSelect();
    };

    return (
        <div className={styles.wrap}>
            <button
                ref={triggerRef}
                type="button"
                aria-label={`More actions for ${label}`}
                aria-haspopup="menu"
                aria-expanded={open}
                className={styles.trigger}
                onClick={() => onOpenChange(!open)}
            >
                <KebabIcon/>
            </button>
            {open && ReactDOM.createPortal(
                <>
                    <div className={styles.backdrop} onClick={() => onOpenChange(false)}/>
                    <div
                        ref={menuRef}
                        role="menu"
                        aria-label={`Actions for ${label}`}
                        className={styles.menu}
                        style={position ? {left: position.left, top: position.top, bottom: position.bottom} : undefined}
                    >
                        <div className={styles.dragHandleRow}><span className={styles.dragHandle}/></div>
                        <span className={styles.caption}>{label}</span>
                        {items.map((item) => (
                            <button
                                key={item.key}
                                type="button"
                                role="menuitem"
                                tabIndex={0}
                                className={`${styles.item} ${item.danger ? styles.danger : ''}`}
                                onClick={() => select(item)}
                            >
                                {item.icon}{item.label}
                            </button>
                        ))}
                        <button type="button" className={styles.mobileCancel} onClick={() => onOpenChange(false)}>
                            Cancel
                        </button>
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};

export default RowMenu;
